import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import AnDateTimePicker from '../../../components/DateTime/DateTimePicker'
import { BookingContext, SectionHeader } from './NewBooking'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { Place } from '@mui/icons-material'
import {
  Alert,
  Autocomplete,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
} from '@mui/material'
import OrganisationSelector from './OrganisationSelector'
import API from '../../../services/API'
import AddressAutocomplete from '../components/AddressAutocomplete'

const hasCoordinates = (location) =>
  Array.isArray(location?.coordinates) && location.coordinates.length === 2

const getId = (value) => (typeof value === 'string' ? value : value?._id)

const Destination = ({ title, level, formData, selectedPatient }) => {
  const [organisationOptions, setOrganisationOptions] = useState([])
  useEffect(() => {
    const loadOrganisationOptions = async () => {
      try {
        API.get('/pts/organisationoptions?type=PTS').then((res) => {
          if (res.status === 200) {
            setOrganisationOptions(res.data)
          }
        })
      } catch (error) {
        console.error('Failed to load organisation/requester options:', error)
      }
    }

    loadOrganisationOptions()
  }, [])

  const {
    existingBooking,
    handleChange,
    setLocationTypeSelected,
  } = useContext(BookingContext)

  const levelForm = formData?.[level] || {}

  const isOrgCustomer =
    !formData?.patientIsCustomer && Boolean(formData?.organisation)
  const customerOrgId = getId(formData?.organisation)
  const stopOrgId =
    getId(levelForm?.organisationAsAddress) ||
    getId(levelForm?.when?.organisationAsAddress)

  const customerOrganisation =
    organisationOptions.find(
      (option) => String(option._id) === String(customerOrgId),
    ) || (typeof formData?.organisation === 'object' ? formData.organisation : null)

  // Tracks that the user explicitly chose a hospital other than the customer's,
  // so an empty selector isn't mistaken for "use the customer's hospital".
  const [otherHospital, setOtherHospital] = useState(
    () =>
      levelForm?.locationType === 'Hospital' &&
      Boolean(stopOrgId) &&
      String(stopOrgId) !== String(customerOrgId),
  )

  // Legacy stops stored a Hospital stop with no organisation, meaning "the
  // customer's hospital" — make that explicit so submit/edit can rely on it.
  useEffect(() => {
    if (
      isOrgCustomer &&
      levelForm?.locationType === 'Hospital' &&
      !stopOrgId &&
      !otherHospital
    ) {
      handleChange(`${level}.organisationAsAddress`, customerOrgId)
    }
  }, [
    isOrgCustomer,
    levelForm?.locationType,
    stopOrgId,
    otherHospital,
    customerOrgId,
  ])

  const fields = [
    { fieldName: 'addressLine1', title: 'Address Line 1' },
    { fieldName: 'addressLine2', title: 'Address Line 2' },
    { fieldName: 'town', title: 'Town' },
    { fieldName: 'city', title: 'City', small: true },
    { fieldName: 'postCode', title: 'Post Code', small: true },
    { fieldName: 'contactNumber', title: 'Contact Number' },
    { fieldName: 'note', title: 'Note', multiline: true },
  ]

  const clearSelectedLocation = (fieldName, value) => {
    handleChange(level, {
      ...levelForm,
      [fieldName]: value,
      location: undefined,
      addressMeta: undefined,
    })
  }

  const selectAddressOption = (option) => {
    handleChange(level, {
      ...levelForm,
      name: option.name || levelForm?.name || '',
      addressLine1: option.addressLine1 || '',
      addressLine2: option.addressLine2 || '',
      town: option.town || '',
      city: option.city || '',
      postCode: option.postCode || '',
      location: {
        type: 'Point',
        coordinates: option.coordinates,
      },
      addressMeta: option.addressMeta,
    })
  }

  const stopFieldsFromOrganisation = (org) => ({
    organisationAsAddress: getId(org) || null,
    name: org?.orgName || '',
    addressLine1: org?.orgAddressLineOne || '',
    addressLine2: org?.orgAddressLineTwo || '',
    town: org?.orgAddressTown || '',
    city: org?.orgAddressCity || '',
    postCode: org?.orgAddressPostCode || '',
    ...(hasCoordinates(org?.location) ? { location: org.location } : {}),
  })

  const stopFieldsFromPatient = () => {
    const demographics = selectedPatient?.demographics || {}

    return {
      name: 'Patient Address',
      addressLine1: demographics.addressLine1 || '',
      addressLine2: demographics.addressLine2 || '',
      town: demographics.town || '',
      city: demographics.city || '',
      postCode: demographics.postCode || '',
      contactNumber: demographics.phoneNumber1 || '',
      ...(hasCoordinates(demographics.location)
        ? { location: demographics.location }
        : {}),
    }
  }

  const applyLocationType = (value) => {
    const base = {
      when: levelForm?.when,
      locationType: value === 'Customer Hospital' ? 'Hospital' : value,
    }

    if (value === 'Customer Hospital') {
      setOtherHospital(false)
      handleChange(level, {
        ...base,
        ...stopFieldsFromOrganisation(
          customerOrganisation || formData?.organisation,
        ),
      })
    } else if (value === 'Hospital') {
      setOtherHospital(true)
      handleChange(level, { ...base, organisationAsAddress: null })
    } else if (value === 'Patient Address' && existingBooking) {
      // On edit the server keeps whatever address the stop carries, so
      // refresh it from the patient; on create the server resolves it.
      handleChange(level, { ...base, ...stopFieldsFromPatient() })
      setOtherHospital(false)
    } else {
      setOtherHospital(false)
      handleChange(level, base)
    }

    setLocationTypeSelected(true)
  }

  const patientAddressText = [
    selectedPatient?.demographics?.addressLine1,
    selectedPatient?.demographics?.addressLine2,
    selectedPatient?.demographics?.town,
    selectedPatient?.demographics?.city,
    selectedPatient?.demographics?.postCode,
  ]
    .filter(Boolean)
    .join(', ')

  const organisationAddressID = stopOrgId || customerOrgId

  const organisationAddress =
    organisationOptions.find(
      (option) => String(option?._id) === String(organisationAddressID),
    ) ||
    (typeof formData?.organisation === 'object' &&
    String(getId(formData.organisation)) === String(organisationAddressID)
      ? formData.organisation
      : null)

  const hospitalAddressText = [
    organisationAddress?.orgName,
    organisationAddress?.orgAddressLineOne,
    organisationAddress?.orgAddressLineTwo,
    organisationAddress?.orgAddressTown,
    organisationAddress?.orgAddressCity,
    organisationAddress?.orgAddressPostCode,
  ]
    .filter(Boolean)
    .join(', ')

  const stopHasCoordinates = hasCoordinates(levelForm?.location)
  const patientHasCoordinates = hasCoordinates(
    selectedPatient?.demographics?.location,
  )
  const hospitalHasCoordinates = hasCoordinates(organisationAddress?.location)

  const lookupRequired =
    levelForm?.locationType === 'Other'
      ? !stopHasCoordinates
      : levelForm?.locationType === 'Patient Address'
        ? !stopHasCoordinates && !patientHasCoordinates
        : levelForm?.locationType === 'Hospital'
          ? // The lookup is a fallback for a CHOSEN organisation without map
            // coordinates — until one is picked there's nothing to look up,
            // so don't show it alongside the organisation selector.
            Boolean(stopOrgId) &&
            !stopHasCoordinates &&
            !hospitalHasCoordinates
          : false

  const selectValue =
    levelForm?.locationType === 'Hospital'
      ? isOrgCustomer &&
        !otherHospital &&
        (!stopOrgId || String(stopOrgId) === String(customerOrgId))
        ? 'Customer Hospital'
        : 'Hospital'
      : levelForm?.locationType || ''

  const locationTypeOptions = [
    { value: 'Patient Address', label: "Patient's Home Address" },
    ...(isOrgCustomer
      ? [
          {
            value: 'Customer Hospital',
            label: `${
              customerOrganisation?.orgName || 'Customer Organisation'
            } (Customer)`,
          },
        ]
      : []),
    {
      value: 'Hospital',
      label: isOrgCustomer
        ? 'Another Hospital / Organisation'
        : 'Hospital / Organisation',
    },
    { value: 'Other', label: 'Other Address' },
  ]

  return (
    <Stack gap={1} width="100%" pt={{ lg: 2 }}>
      <Typography variant="title3">{title}</Typography>
      <FormControl fullWidth>
        <InputLabel>Location Type</InputLabel>
        <Select
          value={selectValue}
          onChange={(e) => applyLocationType(e.target.value)}
          label="Location Type"
          name="locationType"
        >
          {locationTypeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {selectValue === 'Patient Address' && (
            <Typography variant="caption">{patientAddressText}</Typography>
          )}
          {selectValue === 'Customer Hospital' && (
            <Typography variant="caption">{hospitalAddressText}</Typography>
          )}
        </FormHelperText>
      </FormControl>
      {selectValue === 'Hospital' && (
        <OrganisationSelector
          label="Select Hospital or Organisation"
          value={
            levelForm?.organisationAsAddress ??
            levelForm?.when?.organisationAsAddress
          }
          options={organisationOptions}
          showAddButton={false}
          onChange={(org) => {
            setOtherHospital(true)
            handleChange(level, {
              when: levelForm?.when,
              locationType: 'Hospital',
              ...stopFieldsFromOrganisation(org),
            })
          }}
        />
      )}
      {['Patient Address', 'Hospital'].includes(levelForm?.locationType) &&
        lookupRequired && (
        <AddressAutocomplete
          address={levelForm}
          onSelect={selectAddressOption}
          helperText="Optional: choose a result if the stored address is hard to locate on the map."
        />
      )}
      {levelForm?.locationType === 'Other' && (
        <Stack gap={1}>
          {lookupRequired && (
            <AddressAutocomplete
              address={levelForm}
              onSelect={selectAddressOption}
              helperText="Choose a result to fill the address and set the journey map point."
            />
          )}
          <TextField
            value={levelForm?.name ?? ''}
            onChange={(e) => clearSelectedLocation('name', e.target.value)}
            onBlur={(e) => clearSelectedLocation('name', e.target.value)}
            fullWidth
            autoComplete="none"
            label="Location Name"
            helperText={
              level === 'pickup' ? 'i.e. Patient Home' : 'i.e. Outpatients'
            }
          />
          <Grid container spacing={2}>
            {fields.map((x, i) => {
              return (
                <Grid item xs={12} lg={x.small ? 6 : 12} key={i}>
                  <TextField
                    value={levelForm?.[x.fieldName] ?? ''}
                    onChange={(e) =>
                      clearSelectedLocation(x.fieldName, e.target.value)
                    }
                    onBlur={(e) =>
                      clearSelectedLocation(x.fieldName, e.target.value)
                    }
                    fullWidth
                    autoComplete="none"
                    label={x.title}
                    multiline={x.multiline || false}
                  />
                </Grid>
              )
            })}
          </Grid>
        </Stack>
      )}
    </Stack>
  )
}

export const pickupJourneyTypes = [
  'Discharge',
  'Repatriation',
  'Emergency',
  'Risk Assessment',
]
export const dropoffJourneyTypes = ['Outpatient']
export const optionalJourneyTypes = [
  'Interhospital',
  'Transfer',
  'Admission',
  'Other',
]

const Locations = () => {
  const {
    formData,
    handleChange,
    selectedPatient,
    locationTypeSelected,
    setLocationTypeSelected,
  } = useContext(BookingContext)
  const [timeSelector, setTimeSelector] = useState({
    title: 'Patient Ready From',
    field: 'patientReadyFrom',
  })

  const customerSelected = Boolean(
    formData?.patientIsCustomer || formData?.organisation,
  )

  if (!customerSelected) {
    return (
      <Box>
        <SectionHeader
          icon={<Place fontSize="small" />}
          title="Pickup & Drop Off"
        />
        <Alert severity="info">
          Choose the customer first — tick &quot;Patient is the Customer&quot;
          or select an organisation above. The pickup and drop off options
          depend on who is being billed.
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <SectionHeader
        icon={<Place fontSize="small" />}
        title="Pickup & Drop Off"
      />
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          md={6}
          lg={
            optionalJourneyTypes.includes(formData?.journeyType ?? '') ? 4 : 6
          }
        >
          <TextField
            select
            fullWidth
            label="Journey Type"
            value={formData.journeyType || ''}
            onChange={(e) => {
              handleChange('journeyType', e.target.value)
              if (
                e.target.value === 'Emergency' &&
                !formData?.pickup?.when?.patientReadyFrom
              ) {
                handleChange(`pickup.when.patientReadyFrom`, new Date())
              }
              if (e.target.value === 'Risk Assessment') {
                // A home visit with no transport leg — both stops are the
                // patient's home; the server mirrors pickup into dropoff.
                handleChange('pickup.locationType', 'Patient Address')
                handleChange('dropoff.locationType', 'Patient Address')
                return
              }
              if (!locationTypeSelected) {
                if (
                  ['Outpatient', 'Admission', 'Emergency'].includes(
                    e.target.value,
                  )
                ) {
                  handleChange(`pickup.locationType`, 'Patient Address')
                  handleChange('dropoff.locationType', 'Hospital')
                } else if (['Discharge'].includes(e.target.value)) {
                  handleChange('pickup.locationType', 'Hospital')
                  handleChange('dropoff.locationType', 'Patient Address')
                }
              }
            }}
          >
            {[
              'Outpatient',
              'Admission',
              'Discharge',
              'Interhospital',
              'Transfer',
              'Repatriation',
              'Emergency',
              'Risk Assessment',
              'Other',
            ].map((t, i) => (
              <MenuItem key={i} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {/* Target Arrival Time */}
        {dropoffJourneyTypes.includes(formData?.journeyType ?? '') && (
          <Grid item xs={12} md={6}>
            <AnDateTimePicker
              date={
                formData?.['dropoff']?.when?.['targetArrivalTime']
                  ? moment(formData?.['dropoff']?.when?.['targetArrivalTime'])
                  : null
              }
              onChange={(newDate) => {
                handleChange(`dropoff.when.targetArrivalTime`, newDate)
                handleChange('pickup.when.patientReadyFrom', null)
              }}
              fullWidth
              label="Target Arrival / Appointment Time"
              disablePast
            />
          </Grid>
        )}
        {/* Patient Ready From */}
        {pickupJourneyTypes.includes(formData?.journeyType ?? '') && (
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box flex={1}>
                <AnDateTimePicker
                  date={
                    formData?.['pickup']?.when?.['patientReadyFrom']
                      ? moment(formData?.['pickup']?.when?.['patientReadyFrom'])
                      : null
                  }
                  onChange={(newDate) => {
                    handleChange(`pickup.when.patientReadyFrom`, newDate)
                    handleChange('dropoff.when.targetArrivalTime', null)
                  }}
                  fullWidth
                  label={
                    formData?.journeyType === 'Risk Assessment'
                      ? 'Assessment Visit Time'
                      : 'Patient Ready From'
                  }
                  disablePast={formData?.journeyType !== 'Emergency'}
                />
              </Box>
              <Button
                size="small"
                variant="outlined"
                sx={{ minWidth: 58, fontWeight: 800 }}
                onClick={() => {
                  handleChange('pickup.when.patientReadyFrom', moment().add(1, 'minute'))
                  handleChange('dropoff.when.targetArrivalTime', null)
                }}
              >
                Now
              </Button>
            </Stack>
          </Grid>
        )}
        {/* Both */}
        {['Interhospital', 'Transfer', 'Admission', 'Other'].includes(
          formData?.journeyType ?? '',
        ) && (
          <>
            <Grid item xs={12} md={6} lg={4}>
              <Autocomplete
                value={timeSelector}
                onChange={(e, newValue) => {
                  if (!newValue) return

                  if (timeSelector.field === 'patientReadyFrom') {
                    handleChange('pickup.when.patientReadyFrom', null)
                  } else if (timeSelector.field === 'targetArrivalTime') {
                    handleChange('dropoff.when.targetArrivalTime', null)
                  }

                  setTimeSelector(newValue)
                }}
                options={[
                  { title: 'Patient Ready From', field: 'patientReadyFrom' },
                  {
                    title: 'Target Arrival / Appointment Time',
                    field: 'targetArrivalTime',
                  },
                ]}
                getOptionLabel={(opt) => opt?.title || ''}
                isOptionEqualToValue={(opt, value) =>
                  opt?.field === value?.field
                }
                renderInput={(params) => (
                  <TextField {...params} label="Time Selector" />
                )}
                fullWidth
                disableClearable
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              {timeSelector.field === 'patientReadyFrom' ? (
                <AnDateTimePicker
                  date={
                    formData?.['pickup']?.when?.['patientReadyFrom']
                      ? moment(formData?.['pickup']?.when?.['patientReadyFrom'])
                      : null
                  }
                  onChange={(newDate) => {
                    handleChange(`pickup.when.patientReadyFrom`, newDate)
                    handleChange(`dropoff.when.targetArrivalTime`, null)
                  }}
                  fullWidth
                  label="Patient Ready From"
                  disablePast
                />
              ) : timeSelector.field === 'targetArrivalTime' ? (
                <AnDateTimePicker
                  date={
                    formData?.['dropoff']?.when?.['targetArrivalTime']
                      ? moment(
                          formData?.['dropoff']?.when?.['targetArrivalTime'],
                        )
                      : null
                  }
                  onChange={(newDate) => {
                    handleChange(`dropoff.when.targetArrivalTime`, newDate)
                    handleChange('pickup.when.patientReadyFrom', null)
                  }}
                  fullWidth
                  label="Target Arrival Time"
                  disablePast
                />
              ) : null}
            </Grid>
          </>
        )}
        {['Interhospital', 'Transfer', 'Repatriation', 'Other'].includes(
          formData?.journeyType,
        ) && (
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Blue Light Transfer"
              value={formData.blueLightTransfer || ''}
              onChange={(e) =>
                handleChange('blueLightTransfer', e.target.value)
              }
            >
              {['Yes', 'No', 'Crew Discretion'].map((t, i) => (
                <MenuItem key={i} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </Grid>

      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        width="100%"
        gap={{ xs: 2, lg: 4 }}
        divider={<Divider orientation="vertical" flexItem />}
      >
        <Destination
          title={
            formData?.journeyType === 'Risk Assessment'
              ? 'Visit Location'
              : 'Pickup'
          }
          level="pickup"
          formData={formData}
          handleChange={handleChange}
          selectedPatient={selectedPatient}
        />
        {formData?.journeyType !== 'Risk Assessment' && (
          <Destination
            title="Drop Off"
            level="dropoff"
            formData={formData}
            handleChange={handleChange}
            selectedPatient={selectedPatient}
          />
        )}
      </Stack>
    </Box>
  )
}

export default Locations
